import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Search } from "lucide-react";
import { Printer, usePrinterSearch } from "@/hooks/use-printer-search";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PrinterSuggestionForm from "./PrinterSuggestionForm";

interface PrinterSelectorProps {
  selectedPrinters: Printer[];
  onChange: (printers: Printer[]) => void;
  integrated?: boolean;
}

const PrinterSelector: React.FC<PrinterSelectorProps> = ({
  selectedPrinters,
  onChange,
  integrated = false
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const {
    printers,
    manufacturers,
    loading,
    manufacturersLoading,
    searchPrinters,
    loadManufacturers,
    suggestPrinter
  } = usePrinterSearch();
  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      await searchPrinters(query);
    }
  };
  const addPrinter = (printer: Printer) => {
    if (!selectedPrinters.find(p => p.id === printer.id)) {
      onChange([...selectedPrinters, printer]);
    }
    setSearchQuery("");
    setIsPopoverOpen(false);
  };
  const removePrinter = (printerId: string) => {
    onChange(selectedPrinters.filter(p => p.id !== printerId));
  };
  const handleSuggestionSubmit = async (printerName: string, manufacturerName: string) => {
    try {
      const newPrinter = await suggestPrinter(printerName, manufacturerName);
      console.log('Printer suggestion submitted:', {
        printerName,
        manufacturerName
      });

      // Add the suggested printer to selected printers
      if (newPrinter) {
        onChange([...selectedPrinters, newPrinter]);
      }
      setShowSuggestionForm(false);
      setIsPopoverOpen(false);
    } catch (error) {
      console.error('Error submitting printer suggestion:', error);
    }
  };

  // Filter out already selected printers
  const availablePrinters = printers.filter(printer => !selectedPrinters.some(selected => selected.id === printer.id));
  if (integrated) {
    return (
      <div className="space-y-4">
        {/* Single row layout with label, button, and badges */}
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm font-medium">Applicable Printers</Label>
          
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus size={16} className="mr-2" />
                Add Printer
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Card className="border-0 shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Search Printers</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Only show search if not showing suggestion form */}
                  {!showSuggestionForm && (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <Input
                          placeholder="Search printers..."
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          className="pl-10"
                          autoFocus
                        />
                      </div>

                      {loading && (
                        <div className="text-sm text-muted-foreground text-center py-2">
                          Searching...
                        </div>
                      )}

                      {availablePrinters.length > 0 && (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {availablePrinters.slice(0, 10).map((printer) => (
                            <Button
                              key={printer.id}
                              variant="ghost"
                              className="w-full justify-start text-left h-auto py-2"
                              onClick={() => addPrinter(printer)}
                            >
                              <div>
                                <div className="font-medium">{printer.name}</div>
                                <div className="text-sm text-muted-foreground">{printer.manufacturer}</div>
                              </div>
                            </Button>
                          ))}
                        </div>
                      )}

                      {searchQuery && !loading && availablePrinters.length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-sm text-muted-foreground mb-2">
                            Printer not found
                          </p>
                          <Button variant="outline" size="sm" onClick={() => setShowSuggestionForm(true)}>
                            Suggest New Printer
                          </Button>
                        </div>
                      )}

                      {/* Always show suggest button at bottom when not searching */}
                      {!searchQuery && (
                        <div className="border-t pt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => setShowSuggestionForm(true)}
                          >
                            <Plus size={16} className="mr-2" />
                            Suggest New Printer
                          </Button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Suggest Printer Form */}
                  {showSuggestionForm && (
                    <PrinterSuggestionForm
                      manufacturers={manufacturers}
                      manufacturersLoading={manufacturersLoading}
                      onLoadManufacturers={loadManufacturers}
                      onSubmit={handleSuggestionSubmit}
                      onCancel={() => setShowSuggestionForm(false)}
                      isSubmitting={false}
                    />
                  )}
                </CardContent>
              </Card>
            </PopoverContent>
          </Popover>

          {/* Selected Printers Badges */}
          {selectedPrinters.map((printer) => (
            <Badge key={printer.id} variant="secondary" className="flex items-center gap-1">
              {printer.name}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 hover:bg-transparent"
                onClick={() => removePrinter(printer.id)}
              >
                <X size={12} />
              </Button>
            </Badge>
          ))}
        </div>
      </div>
    );
  }

  // Original layout for non-integrated use
  return (
    <div className="space-y-4">
      {/* Header row with label and Add Printer button */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Applicable Printers</Label>
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Plus size={16} className="mr-2" />
              Add Printer
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="end">
            <Card className="border-0 shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Search Printers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Only show search if not showing suggestion form */}
                {!showSuggestionForm && <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <Input placeholder="Search printers..." value={searchQuery} onChange={e => handleSearch(e.target.value)} className="pl-10" autoFocus />
                    </div>

                    {loading && <div className="text-sm text-muted-foreground text-center py-2">
                        Searching...
                      </div>}

                    {availablePrinters.length > 0 && <div className="max-h-48 overflow-y-auto space-y-1">
                        {availablePrinters.slice(0, 10).map(printer => <Button key={printer.id} variant="ghost" className="w-full justify-start text-left h-auto py-2" onClick={() => addPrinter(printer)}>
                            <div>
                              <div className="font-medium">{printer.name}</div>
                              <div className="text-sm text-muted-foreground">{printer.manufacturer}</div>
                            </div>
                          </Button>)}
                      </div>}

                    {searchQuery && !loading && availablePrinters.length === 0 && <div className="text-center py-4">
                        <p className="text-sm text-muted-foreground mb-2">
                          Printer not found
                        </p>
                        <Button variant="outline" size="sm" onClick={() => setShowSuggestionForm(true)}>
                          Suggest New Printer
                        </Button>
                      </div>}

                    {/* Always show suggest button at bottom when not searching */}
                    {!searchQuery && <div className="border-t pt-3">
                        <Button variant="outline" size="sm" className="w-full" onClick={() => setShowSuggestionForm(true)}>
                          <Plus size={16} className="mr-2" />
                          Suggest New Printer
                        </Button>
                      </div>}
                  </>}

                {/* Suggest Printer Form */}
                {showSuggestionForm && <PrinterSuggestionForm manufacturers={manufacturers} manufacturersLoading={manufacturersLoading} onLoadManufacturers={loadManufacturers} onSubmit={handleSuggestionSubmit} onCancel={() => setShowSuggestionForm(false)} isSubmitting={false} />}
              </CardContent>
            </Card>
          </PopoverContent>
        </Popover>
      </div>
      
      {/* Selected Printers */}
      {selectedPrinters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedPrinters.map((printer) => (
            <Badge key={printer.id} variant="secondary" className="flex items-center gap-1">
              {printer.name}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 hover:bg-transparent"
                onClick={() => removePrinter(printer.id)}
              >
                <X size={12} />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrinterSelector;
